/**
 * Applies the AI assistant's `applyEmailEdits` operations to a builder draft.
 *
 * The `applyEmailEdits` tool is declared on the backend but executed here, on
 * the client, against the live editor state. This module is the pure core of
 * that execution: given a draft and a batch of operations, it returns the next
 * draft. Product operations are keyed by SAP article number and resolved to
 * full block data via the catalog (`resolveItem`).
 */

import type { Item } from '@/api/items/items.types.ts';
import type { EmailEditOperation } from '@/api/ai/ai.types';
import type {
  EmailBlock,
  ProductCardBlockData,
  ProductListItem,
  ProductSpotlightBlockData,
  RenderEmailRequest,
} from '@/api/email-builder/email-builder.types.ts';
import { itemLinkFor } from './email-builder.constants';
import { newBlockId } from './hooks/use-email-blocks';
import {
  cardFromItem,
  productListItemFromItem,
  spotlightFromItem,
} from './product-blocks';

/** Resolves a SAP article number to a catalog item, or `undefined` if unknown. */
export type ItemResolver = (itemId: string) => Item | undefined;

export interface ApplyEmailEditsResult {
  /** The draft after every operation was applied, in order. */
  draft: RenderEmailRequest;
  /** Notes for operations that referenced an unknown block id or item. */
  warnings: string[];
}

/** Minimal product-card data when an item could not be resolved from the catalog. */
function fallbackCard(itemId: string): ProductCardBlockData {
  return {
    name: itemId,
    articleNumber: itemId,
    imageUrl: '',
    price: '',
    manufacturer: '',
    href: itemLinkFor(itemId),
  };
}

/** Minimal product-spotlight data when an item could not be resolved. */
function fallbackSpotlight(itemId: string): ProductSpotlightBlockData {
  return {
    name: itemId,
    articleNumber: itemId,
    description: '',
    imageUrl: '',
    price: '',
    manufacturer: '',
    buttonLabel: 'Jetzt bestellen',
    buttonHref: itemLinkFor(itemId),
  };
}

/** Minimal product-list row when an item could not be resolved. */
function fallbackListItem(itemId: string): ProductListItem {
  return { name: itemId, articleNumber: itemId, price: '' };
}

/**
 * Applies `operations` to `draft` in order and returns the resulting draft.
 * Pure — never mutates the input draft or its blocks.
 */
export function applyEmailEdits(
  draft: RenderEmailRequest,
  operations: EmailEditOperation[],
  resolveItem: ItemResolver,
): ApplyEmailEditsResult {
  let subject = draft.subject;
  let preheader = draft.preheader;
  let blocks = [...draft.blocks];
  const warnings: string[] = [];

  /** Inserts a block at `index`, clamped, or appends when `index` is omitted. */
  const insertBlock = (block: EmailBlock, index?: number) => {
    const at =
      index === undefined
        ? blocks.length
        : Math.max(0, Math.min(index, blocks.length));
    blocks.splice(at, 0, block);
  };

  for (const op of operations) {
    switch (op.op) {
      case 'set-meta':
        if (op.subject !== undefined) subject = op.subject;
        if (op.preheader !== undefined) preheader = op.preheader;
        break;

      case 'add-block':
        insertBlock(
          { id: newBlockId(), type: op.block.type, data: op.block.data },
          op.index,
        );
        break;

      case 'update-block': {
        const index = blocks.findIndex((block) => block.id === op.id);
        if (index < 0) {
          warnings.push(`Block „${op.id}“ existiert nicht mehr.`);
          break;
        }
        blocks[index] = {
          id: op.id,
          type: op.block.type,
          data: op.block.data,
        };
        break;
      }

      case 'remove-block': {
        const next = blocks.filter((block) => block.id !== op.id);
        if (next.length === blocks.length) {
          warnings.push(`Block „${op.id}“ existiert nicht mehr.`);
        }
        blocks = next;
        break;
      }

      case 'reorder-blocks': {
        const remaining = new Map(blocks.map((block) => [block.id, block]));
        const ordered: EmailBlock[] = [];
        for (const id of op.orderedIds) {
          const block = remaining.get(id);
          if (block) {
            ordered.push(block);
            remaining.delete(id);
          }
        }
        // Keep any blocks the AI did not mention, in their original order.
        ordered.push(...blocks.filter((block) => remaining.has(block.id)));
        blocks = ordered;
        break;
      }

      case 'add-product': {
        const item = resolveItem(op.itemId);
        if (!item) warnings.push(`Artikel „${op.itemId}“ nicht gefunden.`);
        if (op.variant === 'spotlight') {
          insertBlock(
            {
              id: newBlockId(),
              type: 'product-spotlight',
              data: item ? spotlightFromItem(item) : fallbackSpotlight(op.itemId),
            },
            op.index,
          );
        } else {
          insertBlock(
            {
              id: newBlockId(),
              type: 'product-card',
              data: item ? cardFromItem(item) : fallbackCard(op.itemId),
            },
            op.index,
          );
        }
        break;
      }

      case 'add-product-list': {
        const items = op.itemIds.map((itemId) => {
          const item = resolveItem(itemId);
          if (item) return productListItemFromItem(item);
          warnings.push(`Artikel „${itemId}“ nicht gefunden.`);
          return fallbackListItem(itemId);
        });
        insertBlock(
          {
            id: newBlockId(),
            type: 'product-list',
            data: { title: op.title ?? '', items },
          },
          op.index,
        );
        break;
      }
    }
  }

  return { draft: { subject, preheader, blocks }, warnings };
}
